import { AsyncPipe, NgIf } from '@angular/common';
import { Component, NgZone, OnInit } from '@angular/core';
import { MatProgressBar } from '@angular/material/progress-bar';
import { NavigationError, Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { SignalRService } from 'scw-digital-boards-widgets';
import { GlobalErrorHandlerService } from './core/error-handler/global-error-handler.service';
import { CheckForUpdateService } from './core/services/check-for-update.service';
import { DialogCloseButtonService } from './core/services/dialog-close-button.service';
import { HeartbeatService } from './core/services/heartbeat.service';
import { InternetStatusService } from './core/services/internet-status.service';
import { LoadingService } from './core/services/loading.service';
import { PageHeaderComponent } from './layout/page-header/page-header.component';
import { ToolbarComponent } from './layout/toolbar/toolbar.component';
import { ScreenLockComponent } from './shared/components/screen-lock/screen-lock.component';
import { ThemeService } from './shared/components/theme-switcher/theme.service';

@Component({
  imports: [
    RouterOutlet,
    ToolbarComponent,
    PageHeaderComponent,
    MatProgressBar,
    TranslateModule,
    NgIf,
    AsyncPipe,
    ScreenLockComponent,
  ],
  providers: [HeartbeatService],
  selector: 'app-root',
  styleUrl: './app.component.scss',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit {
  title = 'scw-digital-boards';
  isLoading$: Observable<boolean> = LoadingService.loading$;
  public isIframe = this.isInIframe();
  public urlParams = new URLSearchParams(window.location.search);
  public hideToolbar = this.isHideToolbarRequested();
  public hidePageHeader = this.isHidePageHeaderRequested();

  constructor(
    public translate: TranslateService,
    private readonly signalRService: SignalRService,
    private readonly ngZone: NgZone,
    private readonly heartbeatService: HeartbeatService,
    private internetStatus: InternetStatusService,
    public readonly themeService: ThemeService,
    private readonly checkForUpdateService: CheckForUpdateService,
    private router: Router,
    private readonly dialogCloseButtonService: DialogCloseButtonService,
  ) {
    translate.setDefaultLang('en');
    this.internetStatus.start();
    this.heartbeatService.start();
    this.dialogCloseButtonService.start();
  }

  public ngOnInit(): void {
    this.themeService.initTheme();

    this.ngZone.runOutsideAngular(() => {
      (async (): Promise<void> => {
        await this.signalRService.startConnection();
      })();
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        if (event.error && event.error instanceof Error) {
          const isChunkError: boolean = GlobalErrorHandlerService.handleChunkError({
            err: event.error,
            from: 'router',
          });

          if (isChunkError) {
            return;
          }
        }
      }
    });
  }

  private isInIframe(): boolean {
    return window.self !== window.top;
  }

  private isHideToolbarRequested(): boolean {
    return this.urlParams.get('hideToolbar') === 'true';
  }

  private isHidePageHeaderRequested(): boolean {
    return this.urlParams.get('hidePageHeader') === 'true';
  }
}
